/**
 * Portfolio View Hierarchy Types
 * Type definitions for the three-tier portfolio view system:
 * - My Book (advisor level)
 * - Client Level (multi-portfolio view)
 * - Portfolio Level (single portfolio deep dive)
 */

/**
 * View hierarchy levels
 */
export type ViewLevel = 'my-book' | 'client' | 'portfolio';

/**
 * Account filter with enabled state
 */
export interface AccountFilter {
  id: string;
  name: string;
  type: 'investment' | 'cash' | 'custody';
  currency: string;
  enabled: boolean;
}

/**
 * Asset class filter options
 */
export type AssetClassFilter =
  | 'all'
  | 'equities'
  | 'bonds'
  | 'funds'
  | 'derivatives'
  | 'cash'
  | 'alternative';

/**
 * Currency filter options
 */
export type CurrencyFilter =
  | 'all'
  | 'CHF'
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'JPY';

/**
 * Mandate type filter options
 */
export type MandateFilter =
  | 'all'
  | 'discretionary'
  | 'advisory'
  | 'execution-only';

/**
 * Risk profile filter options
 */
export type RiskFilter =
  | 'all'
  | 'conservative-1-3'
  | 'moderate-4'
  | 'aggressive-5';

/**
 * Performance filter options
 */
export type PerformanceFilter =
  | 'all'
  | 'outperforming'
  | 'underperforming';

/**
 * Multi-portfolio client filter options
 */
export type MultiPortfolioFilter =
  | 'all'
  | 'single'
  | 'multiple';

/**
 * My Book level filters (advisor's entire book)
 */
export interface MyBookFilters {
  /** Mandate type filter (can select multiple) */
  mandate: MandateFilter[];
  /** Risk profile filter (can select multiple) */
  risk: RiskFilter[];
  /** Performance filter (single selection) */
  performance: PerformanceFilter;
  /** Multi-portfolio clients filter (single selection) */
  multiPortfolio: MultiPortfolioFilter;
}

/**
 * Portfolio level filters (reusable at client and portfolio levels)
 * NOTE: View level selection (My Book/Client/Portfolio) is handled by ViewLevelSelector, not filters
 * TASK-207: Extended to include My Book filters for unified filter handling
 */
export interface PortfolioFilters {
  /** Account filters (multi-select with enabled state) */
  accounts: AccountFilter[];
  /** Asset class filters (multi-select) */
  assetClasses: AssetClassFilter[];
  /** Currency filters (multi-select) */
  currencies: CurrencyFilter[];
  /** Date mode: latest or historical */
  dateMode: 'latest' | 'historical';
  /** Historical date (when dateMode is 'historical') */
  historicalDate?: string;
  /** Mandate type filter (can select multiple) - for My Book level */
  mandate?: MandateFilter[];
  /** Risk profile filter (can select multiple) - for My Book level */
  risk?: RiskFilter[];
  /** Performance filter (single selection) - for My Book level */
  performance?: PerformanceFilter;
  /** Multi-portfolio clients filter (single selection) - for My Book level */
  multiPortfolio?: MultiPortfolioFilter;
}

/**
 * Client level filters (extends portfolio filters + comparison)
 */
export interface ClientFilters extends PortfolioFilters {
  /** Portfolio IDs to compare (2+ for comparison view) */
  comparePortfolios: string[];
}

/**
 * View context - tracks current view state and navigation history
 */
export interface ViewContext {
  /** Current view level */
  level: ViewLevel;
  /** Client ID when in client or portfolio view */
  clientId?: string;
  /** Portfolio ID when in portfolio view */
  portfolioId?: string;
  /** Previous view level (for drill-up navigation) */
  previousLevel?: ViewLevel;
}

/**
 * Get default My Book filters
 */
export const getDefaultMyBookFilters = (): MyBookFilters => ({
  mandate: ['all'],
  risk: ['all'],
  performance: 'all',
  multiPortfolio: 'all',
});

/**
 * Get default Portfolio filters
 */
export const getDefaultPortfolioFilters = (): PortfolioFilters => ({
  accounts: [],
  assetClasses: ['all'],
  currencies: ['all'],
  dateMode: 'latest',
  historicalDate: undefined,
});

/**
 * Get default Client filters
 * Inherits default portfolio filters + empty comparison list
 */
export const getDefaultClientFilters = (): ClientFilters => ({
  ...getDefaultPortfolioFilters(),
  comparePortfolios: [],
});

/**
 * Get default View context
 */
export const getDefaultViewContext = (): ViewContext => ({
  level: 'my-book',
  clientId: undefined,
  portfolioId: undefined,
  previousLevel: undefined,
});

/**
 * Helper: Check if filters array includes 'all'
 */
export const hasAllFilter = (filters: any[]): boolean => {
  return filters.includes('all');
};

/**
 * Helper: Remove 'all' from filters array
 */
export const removeAllFilter = <T extends string>(filters: T[]): T[] => {
  return filters.filter(f => f !== 'all') as T[];
};

/**
 * Helper: Get active filters count (excluding 'all')
 */
export const getActiveFiltersCount = (filters: {
  assetClasses?: any[];
  currencies?: any[];
  accounts?: AccountFilter[];
  mandate?: any[];
  risk?: any[];
}): number => {
  let count = 0;

  if (filters.assetClasses && !hasAllFilter(filters.assetClasses)) {
    count += filters.assetClasses.length;
  }

  if (filters.currencies && !hasAllFilter(filters.currencies)) {
    count += filters.currencies.length;
  }

  if (filters.accounts && filters.accounts.some(acc => !acc.enabled)) {
    count += 1; // Count as 1 active filter if not all accounts selected
  }

  if (filters.mandate && !hasAllFilter(filters.mandate)) {
    count += filters.mandate.length;
  }

  if (filters.risk && !hasAllFilter(filters.risk)) {
    count += filters.risk.length;
  }

  return count;
};
